import { useEffect, useState } from 'react';
import { ChevronRight, Layers, Minus, Plus, Search, Trash2, X } from 'lucide-react';
import { getServices } from '../../../api/service.api';
import usePackagePickerStore, { getVariantName, getVariantPrice } from '../../../stores/packagePickerStore';
import { formatCurrency } from '../../../utils/formatters';
import styles from '../../../assets/scss/pages/PackageEditor.module.scss';

// Port từ app: screens/admin/PackageItemPicker.js (chuyển sang modal cho web).
// Chọn dịch vụ vào gói: tab "Có sẵn" (variant-aware) + tab "Dịch vụ thêm" (ảo, chỉ tên).

function getServicePriceText(service) {
    const variants = service?.variants || [];
    if (variants.length === 0) return formatCurrency(service.price);
    const prices = variants.map(getVariantPrice).filter((p) => p > 0);
    if (prices.length === 0) return formatCurrency(service.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return formatCurrency(min);
    return `${formatCurrency(min)} - ${formatCurrency(max)}`;
}

function hasSaleInVariants(service) {
    return (service?.variants || []).some((v) => Number(v.salePrice) > 1000);
}

export default function PackageItemPicker({ open, onClose }) {
    const items = usePackagePickerStore((s) => s.items);
    const addExistingWithVariant = usePackagePickerStore((s) => s.addExistingWithVariant);
    const addVirtual = usePackagePickerStore((s) => s.addVirtual);
    const removeAt = usePackagePickerStore((s) => s.removeAt);
    const removeBy = usePackagePickerStore((s) => s.removeBy);
    const updateQtyBy = usePackagePickerStore((s) => s.updateQtyBy);

    const [tab, setTab] = useState('existing');
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [virtualName, setVirtualName] = useState('');
    const [virtualQty, setVirtualQty] = useState('1');
    const [selectedService, setSelectedService] = useState(null); // service đang mở chọn biến thể

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        getServices({ limit: 200, typeService: 'dichvu' })
            .then((res) => {
                if (alive) setServices(res?.data?.services || []);
            })
            .catch(() => {
                if (alive) setServices([]);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [open]);

    if (!open) return null;

    const filteredServices = services.filter((svc) => {
        if (svc.isDeleted) return false;
        if (!search) return true;
        return svc.name?.toLowerCase().includes(search.toLowerCase());
    });

    const findItem = (sourceServiceId, sourceVariantId) =>
        items.find(
            (it) => it.sourceServiceId === sourceServiceId && it.sourceVariantId === sourceVariantId,
        );

    const qtyForService = (sourceServiceId) =>
        items
            .filter((it) => it.sourceServiceId === sourceServiceId && !it.isVirtual)
            .reduce((sum, it) => sum + (it.quantity || 0), 0);

    const virtualItems = items.filter((it) => it.isVirtual);
    const totalSelected = items.length;

    const handleClickService = (svc) => {
        const variants = svc.variants || [];
        if (variants.length > 1) {
            setSelectedService(svc);
            return;
        }
        addExistingWithVariant(svc, variants[0] || null);
    };

    const handleAddVirtual = () => {
        if (!virtualName.trim()) return;
        addVirtual(virtualName, virtualQty);
        setVirtualName('');
        setVirtualQty('1');
    };

    const renderQtyBox = (svcId, variantId, item) => (
        <div className={styles.pkgQtyBox} onClick={(e) => e.stopPropagation()}>
            <button
                className={styles.pkgQtyBtn}
                onClick={() => {
                    if (item.quantity <= 1) removeBy(svcId, variantId);
                    else updateQtyBy(svcId, variantId, item.quantity - 1);
                }}
            >
                <Minus size={14} />
            </button>
            <input
                className={styles.pkgQtyInput}
                value={String(item.quantity)}
                onChange={(e) => updateQtyBy(svcId, variantId, e.target.value.replace(/\D/g, ''))}
            />
            <button
                className={styles.pkgQtyBtn}
                onClick={() => updateQtyBy(svcId, variantId, item.quantity + 1)}
            >
                <Plus size={14} />
            </button>
        </div>
    );

    const renderServiceRow = (svc) => {
        const variants = svc.variants || [];
        const hasMulti = variants.length > 1;
        const singleVariantId = variants.length === 1 ? variants[0].id : null;
        const inCartItem = hasMulti ? null : findItem(svc.id, singleVariantId);
        const totalQtyMulti = hasMulti ? qtyForService(svc.id) : 0;
        const onSale = hasSaleInVariants(svc);
        const isSelected = !!inCartItem || totalQtyMulti > 0;

        return (
            <div
                key={svc.id}
                className={`${styles.pkgPickRow}${isSelected ? ` ${styles.pkgPickRowActive}` : ''}`}
                onClick={() => handleClickService(svc)}
            >
                <div className={styles.pkgPickImg}>
                    {svc.urlImg ? <img src={svc.urlImg} alt={svc.name} /> : <Layers size={20} />}
                </div>
                <div className={styles.pkgPickInfo}>
                    <span className={styles.pkgPickName}>{svc.name}</span>
                    <span className={styles.pkgPickPriceRow}>
                        <span className={styles.pkgPickPrice}>{getServicePriceText(svc)}</span>
                        {onSale && <span className={styles.pkgSaleTag}>SALE</span>}
                    </span>
                    {hasMulti && (
                        <span className={styles.pkgVariantHint}>
                            <Layers size={11} /> {variants.length} biến thể
                            {totalQtyMulti > 0 ? ` · đã chọn ${totalQtyMulti}` : ''}
                        </span>
                    )}
                </div>
                {hasMulti ? (
                    <ChevronRight size={20} className={styles.pkgPickChevron} />
                ) : inCartItem ? (
                    renderQtyBox(svc.id, singleVariantId, inCartItem)
                ) : (
                    <Plus size={26} className={styles.pkgPickAdd} />
                )}
            </div>
        );
    };

    const renderVariantRow = (variant) => {
        if (!selectedService) return null;
        const inCartItem = findItem(selectedService.id, variant.id);
        const variantLabel = getVariantName(variant) || 'Mặc định';
        const price = getVariantPrice(variant);
        return (
            <div
                key={variant.id}
                className={styles.pkgVariantRow}
                onClick={() => addExistingWithVariant(selectedService, variant)}
            >
                <div className={styles.pkgPickInfo}>
                    <span className={styles.pkgPickName}>{variantLabel}</span>
                    <span className={styles.pkgPickPriceRow}>
                        {Number(variant.salePrice) > 1000 ? (
                            <>
                                <span className={styles.pkgPriceStrike}>
                                    {formatCurrency(variant.retailPrice)}
                                </span>
                                <span className={styles.pkgPickPrice}>
                                    {formatCurrency(variant.salePrice)}
                                </span>
                            </>
                        ) : (
                            <span className={styles.pkgPickPrice}>{formatCurrency(price)}</span>
                        )}
                    </span>
                </div>
                {inCartItem ? (
                    renderQtyBox(selectedService.id, variant.id, inCartItem)
                ) : (
                    <Plus size={24} className={styles.pkgPickAdd} />
                )}
            </div>
        );
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modalBox} ${styles.pkgPickerModal}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>Chọn dịch vụ vào gói</h3>
                    <button className={styles.modalClose} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className={styles.pkgPickerTabs}>
                    <button
                        className={`${styles.pkgPickerTab}${tab === 'existing' ? ` ${styles.pkgPickerTabActive}` : ''}`}
                        onClick={() => setTab('existing')}
                    >
                        Có sẵn
                    </button>
                    <button
                        className={`${styles.pkgPickerTab}${tab === 'virtual' ? ` ${styles.pkgPickerTabActive}` : ''}`}
                        onClick={() => setTab('virtual')}
                    >
                        Dịch vụ thêm ({virtualItems.length})
                    </button>
                </div>

                <div className={styles.pkgPickerBody}>
                    {tab === 'existing' ? (
                        <>
                            <div className={styles.pkgSearchBox}>
                                <Search size={16} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Tìm dịch vụ..."
                                />
                            </div>
                            {loading ? (
                                <div className={styles.pkgPickerEmpty}>Đang tải dịch vụ...</div>
                            ) : filteredServices.length === 0 ? (
                                <div className={styles.pkgPickerEmpty}>Không tìm thấy dịch vụ</div>
                            ) : (
                                <div className={styles.pkgPickerList}>
                                    {filteredServices.map(renderServiceRow)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.pkgPickerList}>
                            <div className={styles.pkgVirtualForm}>
                                <label className={styles.formLabel}>Tên dịch vụ thêm</label>
                                <input
                                    className={styles.formInput}
                                    value={virtualName}
                                    onChange={(e) => setVirtualName(e.target.value)}
                                    placeholder="Vd: Tặng 1 chai serum"
                                />
                                <label className={styles.formLabel}>Số lượng/buổi</label>
                                <input
                                    className={styles.formInput}
                                    value={virtualQty}
                                    onChange={(e) => setVirtualQty(e.target.value.replace(/\D/g, ''))}
                                    placeholder="1"
                                />
                                <button className={styles.pkgAddVirtualBtn} onClick={handleAddVirtual}>
                                    <Plus size={16} /> Thêm dịch vụ
                                </button>
                            </div>
                            <div className={styles.pkgSectionTitle}>Đã thêm ({virtualItems.length})</div>
                            {virtualItems.length === 0 ? (
                                <div className={styles.pkgPickerEmpty}>Chưa có dịch vụ thêm</div>
                            ) : (
                                virtualItems.map((it) => {
                                    const idx = items.indexOf(it);
                                    return (
                                        <div key={`v-${idx}`} className={styles.pkgVirtualRow}>
                                            <div className={styles.pkgPickInfo}>
                                                <span className={styles.pkgPickName}>{it.name}</span>
                                                <span className={styles.pkgPickPrice}>×{it.quantity}/buổi</span>
                                            </div>
                                            <button
                                                className={styles.pkgRemoveBtn}
                                                onClick={() => removeAt(idx)}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.pkgPickerFooter}>
                    <span className={styles.pkgPickerTotal}>
                        Đã chọn <strong>{totalSelected}</strong> dịch vụ
                    </span>
                    <button className={styles.pkgDoneBtn} onClick={onClose}>
                        Hoàn thành
                    </button>
                </div>

                {/* Modal chọn biến thể (con) */}
                {selectedService && (
                    <div className={styles.modalOverlay} onClick={() => setSelectedService(null)}>
                        <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h3 className={styles.modalTitle}>{selectedService.name}</h3>
                                <button className={styles.modalClose} onClick={() => setSelectedService(null)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <p className={styles.modalSubtitle}>Chọn biến thể muốn thêm vào gói</p>
                            <div className={styles.pkgPickerList}>
                                {(selectedService.variants || []).map(renderVariantRow)}
                            </div>
                            <div className={styles.modalFooter}>
                                <button className={styles.pkgDoneBtn} onClick={() => setSelectedService(null)}>
                                    Xong
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
